import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table";
import { Plus, Pencil, Trash2, History } from "lucide-react";
import { API } from "../../../App";
import {
  fmtDate, minutesToHM,
  classifyFlight, FLIGHT_TYPE_LABELS, FLIGHT_BADGE_LABEL,
  FLIGHT_ROW_DESKTOP_CLASS, FLIGHT_ROW_MOBILE_CLASS,
} from "./diUtils";
import FlightForm from "./FlightForm";

export function DiPagination({ page, totalPages, total, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between text-[11px] text-slate-400 border-t border-cockpit-border pt-2 mt-2 flex-wrap gap-2">
      <span className="text-slate-500">{total} voo(s) · página {page} de {totalPages}</span>
      <div className="flex gap-1">
        <button disabled={page <= 1} onClick={() => onPageChange(1)} className="px-1.5 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800 transition-colors" title="Primeira página">«</button>
        <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="px-2 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800 transition-colors">‹ Anterior</button>
        <button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className="px-2 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800 transition-colors">Próximo ›</button>
        <button disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} className="px-1.5 py-0.5 rounded border border-slate-700 disabled:opacity-30 hover:bg-slate-800 transition-colors" title="Última página">»</button>
      </div>
    </div>
  );
}

export default function FlightLogDialog({ open, instructor, onClose, onChanged, onOpenAudit }) {
  const [flights, setFlights] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [showFlightForm, setShowFlightForm] = useState(false);
  const [editingFlight, setEditingFlight] = useState(null);
  const PAGE_SIZE = 50;

  const loadFlights = useCallback(async (p = 1) => {
    if (!instructor?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/di/instructors/${instructor.id}/flights?page=${p}&page_size=${PAGE_SIZE}`,
        { withCredentials: true }
      );
      setFlights(r.data.data || []);
      setPagination({ page: r.data.page || 1, total: r.data.total || 0, totalPages: r.data.total_pages || 1 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar");
    } finally {
      setLoading(false);
    }
  }, [instructor?.id]);

  const refresh = useCallback(() => loadFlights(pagination.page), [loadFlights, pagination.page]);

  useEffect(() => {
    if (open) loadFlights(1);
  }, [open, instructor?.id]); // eslint-disable-line

  const removeFlight = async (fid) => {
    if (!window.confirm("Apagar este voo?")) return;
    try {
      await axios.delete(`${API}/di/instructors/${instructor.id}/flights/${fid}`, { withCredentials: true });
      toast.success("Voo eliminado");
      onChanged?.();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  if (!instructor) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-cockpit-panel border-cockpit-border w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="di-flight-log-dialog">
        <DialogHeader>
          <DialogTitle className="text-orange-300 flex items-center gap-2">
            Diário de voo — {instructor.name} (PU {instructor.pu_number})
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500 text-sm">A carregar...</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                Diário de voo ({pagination.total})
              </h3>
              <div className="flex gap-2">
                {onOpenAudit && (
                  <Button size="sm" variant="outline" onClick={onOpenAudit} data-testid="di-open-audit-btn">
                    <History className="w-4 h-4 mr-1" /> Histórico
                  </Button>
                )}
                <Button size="sm" className="btn-cockpit" onClick={() => { setEditingFlight(null); setShowFlightForm(true); }} data-testid="di-add-flight-btn">
                  <Plus className="w-4 h-4 mr-1" /> Novo voo
                </Button>
              </div>
            </div>

            {flights.length ? (
              <>
                {/* Mobile */}
                <div className="md:hidden space-y-2">
                  {flights.map((f) => {
                    const cls = classifyFlight(f);
                    return (
                      <div key={f.id} className={`p-3 rounded border space-y-2 ${FLIGHT_ROW_MOBILE_CLASS[cls]}`} data-testid={`di-flight-card-${f.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs">
                            <div className="font-mono text-slate-200">{fmtDate(f.flight_date)}</div>
                            <div className="text-slate-400 flex items-center gap-1.5 flex-wrap">
                              {FLIGHT_TYPE_LABELS[f.flight_type] || f.flight_type}
                              {cls === "dc-received" && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-teal-300 bg-teal-500/20 px-1.5 py-0.5 rounded">DC RECEBIDO</span>}
                              {(cls === "instruction-missing" || cls === "instruction-forbidden") && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-red-300 bg-red-500/20 px-1.5 py-0.5 rounded">{FLIGHT_BADGE_LABEL[cls]}</span>}
                              {cls === "instruction" && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-semibold text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded">DEU INSTRUÇÃO</span>}
                            </div>
                            {f.role === "companion" && f.actor_instructor_name && (
                              <div className="text-[10px] text-teal-400/80 mt-0.5" data-testid={`di-flight-from-instructor-${f.id}`}>
                                Instrutor: {f.actor_instructor_name}{f.actor_instructor_pu && <span className="text-slate-500"> · PU {f.actor_instructor_pu}</span>}
                              </div>
                            )}
                            {f.pre_promotion && <div className="text-[9px] text-slate-500 mt-0.5 italic">Período como piloto</div>}
                            {f.companion_name && f.role !== "companion" && (
                              <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`di-flight-companion-${f.id}`}>
                                c/ {f.companion_name}
                                {f.companion_kind === "pilot" && <span className="text-emerald-400/80"> · DC</span>}
                                {f.companion_kind === "instructor" && <span className="text-orange-400/80"> · 2º Instr</span>}
                                {f.companion_kind === "external" && <span className="text-slate-500"> · avulso</span>}
                              </div>
                            )}
                            {f.is_invalid && <div className="text-[10px] text-amber-400 mt-0.5 font-semibold" data-testid={`di-flight-invalid-${f.id}`}>⚠ Inválido</div>}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {f.role === "companion" ? (
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 px-2 py-1 rounded border border-slate-700/50 bg-slate-800/40" title="Voo registado por outro instrutor." data-testid={`di-flight-readonly-${f.id}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                Só leitura
                              </div>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingFlight(f); setShowFlightForm(true); }} data-testid={`di-flight-edit-${f.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => removeFlight(f.id)} data-testid={`di-flight-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div>
                            <div className="text-slate-500 uppercase tracking-wider text-[9px]">{f.role === "companion" ? "DC" : "PIC"}</div>
                            <div className={`font-mono ${f.role === "companion" ? "text-teal-300 font-semibold" : "text-slate-200"}`}>
                              {f.role === "companion" ? minutesToHM(f.dc_minutes) : minutesToHM(f.pic_minutes)}
                            </div>
                          </div>
                          <div>
                            {f.role !== "companion" ? (
                              <><div className="text-slate-500 uppercase tracking-wider text-[9px]">Instrução</div><div className={`font-mono ${(cls === "instruction-missing" || cls === "instruction-forbidden") ? "text-red-300 font-bold" : "text-slate-200"}`}>{minutesToHM(f.instructor_minutes)}</div></>
                            ) : (
                              <><div className="text-slate-500 uppercase tracking-wider text-[9px]">Aeronave</div><div className="text-slate-200">{f.aircraft_registration || "—"}</div></>
                            )}
                          </div>
                          {f.role !== "companion" && <div><div className="text-slate-500 uppercase tracking-wider text-[9px]">Aeronave</div><div className="text-slate-200">{f.aircraft_registration || "—"}</div></div>}
                          <div><div className="text-slate-500 uppercase tracking-wider text-[9px]">Diário/Serviço</div><div className="text-slate-300">{[f.navigation_log_number, f.service_number].filter(Boolean).join(" / ") || "—"}</div></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop */}
                <div className="hidden md:block rounded border border-cockpit-border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-cockpit-border">
                        <TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>PIC</TableHead><TableHead>Instrução</TableHead><TableHead>Aeronave</TableHead><TableHead>Diário/Serviço</TableHead><TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flights.map((f) => {
                        const cls = classifyFlight(f);
                        return (
                          <TableRow key={f.id} className={`border-cockpit-border ${FLIGHT_ROW_DESKTOP_CLASS[cls]}`}>
                            <TableCell className="font-mono text-xs">{fmtDate(f.flight_date)}</TableCell>
                            <TableCell className="text-xs">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span>{FLIGHT_TYPE_LABELS[f.flight_type] || f.flight_type}</span>
                                {cls === "dc-received" && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-teal-300 bg-teal-500/20 px-1.5 py-0.5 rounded">DC RECEBIDO</span>}
                                {(cls === "instruction-missing" || cls === "instruction-forbidden") && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-red-300 bg-red-500/20 px-1.5 py-0.5 rounded" title={cls === "instruction-missing" ? "Voo deveria ter horas de instrução registadas" : "Voo deste tipo não pode ter horas de instrução"}>{FLIGHT_BADGE_LABEL[cls]}</span>}
                                {cls === "instruction" && <span className="inline-flex items-center gap-1 text-[9px] uppercase font-semibold text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded">DEU INSTRUÇÃO</span>}
                              </div>
                              {f.role === "companion" && f.actor_instructor_name && <div className="text-[10px] text-teal-400/80 mt-0.5" data-testid={`di-flight-from-instructor-desk-${f.id}`}>Instrutor: {f.actor_instructor_name}{f.actor_instructor_pu && <span className="text-slate-500"> · PU {f.actor_instructor_pu}</span>}</div>}
                              {f.pre_promotion && <div className="text-[9px] text-slate-500 mt-0.5 italic">Período como piloto</div>}
                              {f.companion_name && f.role !== "companion" && <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`di-flight-companion-desk-${f.id}`}>c/ {f.companion_name}{f.companion_kind === "pilot" && <span className="text-emerald-400/80"> · DC</span>}{f.companion_kind === "instructor" && <span className="text-orange-400/80"> · 2º Instr</span>}{f.companion_kind === "external" && <span className="text-slate-500"> · avulso</span>}</div>}
                              {f.is_invalid && <div className="text-[10px] text-amber-400 mt-0.5 font-semibold" data-testid={`di-flight-invalid-desk-${f.id}`}>⚠ Inválido</div>}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{f.role === "companion" ? <span className="text-teal-300 font-semibold">{minutesToHM(f.dc_minutes)} <span className="text-[9px] text-teal-400/60 font-normal">DC</span></span> : minutesToHM(f.pic_minutes)}</TableCell>
                            <TableCell className={`font-mono text-xs ${(cls === "instruction-missing" || cls === "instruction-forbidden") ? "text-red-300 font-bold" : ""}`}>{f.role === "companion" ? <span className="text-slate-500">—</span> : minutesToHM(f.instructor_minutes)}</TableCell>
                            <TableCell className="text-xs">{f.aircraft_registration || "—"}</TableCell>
                            <TableCell className="text-xs text-slate-400">{[f.navigation_log_number, f.service_number].filter(Boolean).join(" / ") || "—"}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {f.role === "companion" ? (
                                  <div className="flex items-center gap-1 text-[10px] text-slate-500 px-2 py-1 rounded border border-slate-700/50 bg-slate-800/40" title="Voo registado por outro instrutor." data-testid={`di-flight-readonly-desk-${f.id}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                    Só leitura
                                  </div>
                                ) : (
                                  <>
                                    <Button size="icon" variant="ghost" onClick={() => { setEditingFlight(f); setShowFlightForm(true); }} data-testid={`di-flight-edit-desk-${f.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button size="icon" variant="ghost" onClick={() => removeFlight(f.id)} className="text-red-400" data-testid={`di-flight-delete-desk-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500 text-sm border border-dashed border-cockpit-border rounded">
                Sem voos registados.
              </div>
            )}
            <DiPagination page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} onPageChange={(p) => loadFlights(p)} />
          </div>
        )}

        {showFlightForm && (
          <FlightForm
            instructorId={instructor.id}
            instructor={instructor}
            flight={editingFlight}
            onClose={() => setShowFlightForm(false)}
            onSaved={() => { setShowFlightForm(false); onChanged?.(); refresh(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
